/**
 * Licensed module packs — keep in sync with backend/src/module-management/module-packs.ts
 */
export const MODULE_GROUP_KEYS = [
  'documents',
  'approvals',
  'e_forms',
  'accounting',
  'crew',
  'mga',
  'wrh',
  'shipping',
  'spares',
  'pms',
  'operations',
  'core_reference',
  'admin_settings',
] as const;

export type ModuleGroupKey = (typeof MODULE_GROUP_KEYS)[number];

export const ALWAYS_ON_MODULE_GROUP_KEYS: ReadonlyArray<ModuleGroupKey> = [
  'core_reference',
  'admin_settings',
];

export const TOGGLEABLE_MODULE_GROUP_KEYS: ReadonlyArray<ModuleGroupKey> =
  MODULE_GROUP_KEYS.filter((k) => !ALWAYS_ON_MODULE_GROUP_KEYS.includes(k));

/** Groups shown in module management UI. */
export const DISPLAYED_MODULE_GROUP_KEYS: ReadonlyArray<ModuleGroupKey> = [
  ...TOGGLEABLE_MODULE_GROUP_KEYS,
];

export const MODULE_GROUP_LABELS: Record<ModuleGroupKey, string> = {
  documents: 'Documents',
  approvals: 'Approvals',
  e_forms: 'E-Forms',
  accounting: 'Finance',
  crew: 'Crew',
  mga: 'MGA',
  wrh: 'Rest Hours (WRH)',
  shipping: 'Purchasing · Supplies',
  spares: 'Purchasing · Spares',
  pms: 'PMS',
  operations: 'Operations',
  core_reference: 'Core reference data',
  admin_settings: 'System',
};

export const MODULE_GROUP_DESCRIPTIONS: Record<ModuleGroupKey, string> = {
  documents: 'Document library and business rules',
  approvals: 'Approval inbox, delegations, and templates',
  e_forms: 'Dynamic form builder, fill, and searchable submissions',
  accounting: 'Journals, invoices, payments, COA, reports — also unlocks Finance setup',
  crew: 'Crew pool, lists, certificates, reports — also unlocks Crew reference data',
  mga: 'Master General Account',
  wrh: 'Work/rest hours days, compliance, and weekly reports',
  shipping: 'Supply purchasing flow — also unlocks Purchasing reference (supplies)',
  spares: 'Spare purchasing flow — also unlocks Purchasing reference (spares)',
  pms: 'Maintenance hierarchy and work orders — also unlocks PMS lookups',
  operations: 'Noon reports and fleet operational reporting',
  core_reference: 'Always on: vessels, ports, entities, countries, currencies, departments',
  admin_settings: 'Always on: users, application settings, imports, and tools',
};

/** Sidebar composite keys → underlying packs (enabled if any constituent is on). */
export const MODULE_GROUP_ALIASES: Record<string, ReadonlyArray<ModuleGroupKey>> = {
  hr: ['crew', 'mga', 'wrh'],
  purchasing: ['shipping', 'spares'],
  crew_parameters: ['crew'],
  pms_parameters: ['pms'],
  parameters: MODULE_GROUP_KEYS,
};

export type ModuleEntitlements = Record<string, boolean>;

export function isAlwaysOnModuleGroup(key: string): boolean {
  return (ALWAYS_ON_MODULE_GROUP_KEYS as readonly string[]).includes(key);
}

export function isModuleGroupEnabled(
  key: string,
  entitlements: ModuleEntitlements | null | undefined,
): boolean {
  if (isAlwaysOnModuleGroup(key)) return true;

  const alias = MODULE_GROUP_ALIASES[key];
  if (alias) {
    return alias.some((k) => isModuleGroupEnabled(k, entitlements));
  }

  if (!entitlements || !(key in entitlements)) return true;
  return entitlements[key] !== false;
}

export const moduleManagementConfig = {
  route: '/admin/module-management',
  api: {
    entitlements: '/module-management/entitlements',
  },
};

type PathGroupRule = {
  prefix: string;
  /** One or more packs; path is allowed if any listed pack is enabled. */
  moduleGroupKeys: ModuleGroupKey[];
};

/**
 * Path prefix → module pack(s). Longest prefix wins.
 * Shared paths (port-calls, forwardings) list both purchasing packs.
 */
export const PATH_TO_MODULE_GROUP: PathGroupRule[] = [
  // System (always on)
  { prefix: '/admin/module-management', moduleGroupKeys: ['admin_settings'] },
  { prefix: '/admin/settings', moduleGroupKeys: ['admin_settings'] },
  { prefix: '/admin/users', moduleGroupKeys: ['admin_settings'] },
  { prefix: '/admin/ai-billing', moduleGroupKeys: ['admin_settings'] },
  { prefix: '/admin/database-browser', moduleGroupKeys: ['admin_settings'] },
  { prefix: '/admin/import-actions/impa-code', moduleGroupKeys: ['shipping'] },
  { prefix: '/admin/import-actions/accounts-plan', moduleGroupKeys: ['accounting'] },
  { prefix: '/admin/import-actions/companies', moduleGroupKeys: ['accounting'] },
  { prefix: '/admin/import-actions/journals', moduleGroupKeys: ['accounting'] },
  { prefix: '/admin/', moduleGroupKeys: ['admin_settings'] },
  { prefix: '/system/mga-sheet-columns', moduleGroupKeys: ['mga'] },
  { prefix: '/system/', moduleGroupKeys: ['admin_settings'] },
  // Account settings is personal (profile / password / prefs) — not an admin pack.

  // Documents & Approvals & E-Forms
  { prefix: '/documents', moduleGroupKeys: ['documents'] },
  { prefix: '/approvals', moduleGroupKeys: ['approvals'] },
  { prefix: '/e-forms', moduleGroupKeys: ['e_forms'] },

  // Finance
  { prefix: '/accounting', moduleGroupKeys: ['accounting'] },

  // HR
  { prefix: '/crew', moduleGroupKeys: ['crew'] },
  { prefix: '/mga', moduleGroupKeys: ['mga'] },
  { prefix: '/wrh', moduleGroupKeys: ['wrh'] },

  // Purchasing (shared + pack-owned)
  { prefix: '/port-calls', moduleGroupKeys: ['shipping', 'spares'] },
  { prefix: '/forwardings', moduleGroupKeys: ['shipping', 'spares'] },
  { prefix: '/supplies', moduleGroupKeys: ['shipping'] },
  { prefix: '/spares', moduleGroupKeys: ['spares'] },

  // PMS
  { prefix: '/pms', moduleGroupKeys: ['pms'] },

  // Operations
  { prefix: '/operations', moduleGroupKeys: ['operations'] },

  // Core reference
  { prefix: '/parameters/vessels', moduleGroupKeys: ['core_reference'] },
  { prefix: '/parameters/ports', moduleGroupKeys: ['core_reference'] },
  { prefix: '/parameters/countries', moduleGroupKeys: ['core_reference'] },
  { prefix: '/parameters/currencies', moduleGroupKeys: ['core_reference'] },
  { prefix: '/parameters/departments', moduleGroupKeys: ['core_reference'] },
  { prefix: '/parameters/general', moduleGroupKeys: ['core_reference'] },
  { prefix: '/parameters/crew', moduleGroupKeys: ['crew'] },
  { prefix: '/parameters/purchasing', moduleGroupKeys: ['shipping', 'spares'] },
  { prefix: '/parameters/supplies-catalog', moduleGroupKeys: ['shipping'] },
  { prefix: '/parameters/spares-catalog', moduleGroupKeys: ['spares'] },
  { prefix: '/parameters/pms', moduleGroupKeys: ['pms'] },
  { prefix: '/parameters/finance', moduleGroupKeys: ['accounting'] },
  { prefix: '/parameters/store-categories', moduleGroupKeys: ['shipping'] },
  { prefix: '/parameters', moduleGroupKeys: ['core_reference'] },
  { prefix: '/entities', moduleGroupKeys: ['core_reference'] },
  { prefix: '/suppliers', moduleGroupKeys: ['core_reference'] },
];

/** Paths that are never guarded by entitlements. */
const UNGUARDED_PATHS = ['/dashboard', '/notifications', '/bookmarks'];

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return (
    pathname === prefix ||
    (pathname.startsWith(prefix) &&
      (pathname.length === prefix.length || pathname[prefix.length] === '/'))
  );
}

/** Resolve pack keys for a path (empty = unguarded / always allowed). */
export function getModuleGroupKeysForPath(pathname: string): ModuleGroupKey[] {
  if (UNGUARDED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
    return [];
  }
  const sorted = [...PATH_TO_MODULE_GROUP].sort((a, b) => b.prefix.length - a.prefix.length);
  for (const { prefix, moduleGroupKeys } of sorted) {
    if (pathMatchesPrefix(pathname, prefix)) return moduleGroupKeys;
  }
  return [];
}

/** @deprecated Prefer getModuleGroupKeysForPath — returns first key or null for unguarded. */
export function getModuleGroupKeyForPath(pathname: string): ModuleGroupKey | null {
  const keys = getModuleGroupKeysForPath(pathname);
  return keys[0] ?? null;
}

/** Path is available when unguarded, or any of its packs is enabled. */
export function isPathEntitled(
  pathname: string,
  entitlements: ModuleEntitlements | null | undefined,
): boolean {
  const keys = getModuleGroupKeysForPath(pathname);
  if (keys.length === 0) return true;
  return keys.some((k) => isModuleGroupEnabled(k, entitlements));
}
